import type { CaptureForm } from '../../../mocks/leadCapture';

interface Props {
  form: CaptureForm;
  responsesCount: number;
  newCount: number;
  onEdit: (form: CaptureForm) => void;
  onEmbed: (form: CaptureForm) => void;
  onToggleStatus: (formId: string) => void;
  onViewResponses: (form: CaptureForm) => void;
}

const statusColors: Record<string, string> = {
  Active: 'bg-emerald-50 text-emerald-700',
  Inactive: 'bg-gray-100 text-gray-500',
  Draft: 'bg-amber-50 text-amber-700',
};

export default function FormCard({ form, responsesCount, newCount, onEdit, onEmbed, onToggleStatus, onViewResponses }: Props) {
  const convRate = form.submissions > 0 ? Math.round((form.converted / form.submissions) * 100) : 0;
  const viewRate = form.views > 0 ? Math.round((form.submissions / form.views) * 100) : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden transition-all hover:border-gray-200">
      {/* Top bar */}
      <div className="h-1 w-full" style={{ backgroundColor: form.status === 'Active' ? form.color : '#e5e7eb' }} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: `${form.color}18` }}
            >
              <i className={`${form.icon} text-lg`} style={{ color: form.color }} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800 leading-tight">{form.name}</h3>
              <p className="text-xs text-gray-400 mt-0.5">{form.category} · {form.fields.length} fields</p>
            </div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap flex-shrink-0 ${statusColors[form.status]}`}>
            {form.status}
          </span>
        </div>

        <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">{form.description}</p>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-2.5 bg-gray-50 rounded-lg">
            <p className="text-base font-bold text-gray-800">{form.views.toLocaleString()}</p>
            <p className="text-xs text-gray-400 mt-0.5">Views</p>
          </div>
          <div className="text-center p-2.5 bg-gray-50 rounded-lg">
            <p className="text-base font-bold text-gray-800">{form.submissions.toLocaleString()}</p>
            <p className="text-xs text-gray-400 mt-0.5">Submissions</p>
          </div>
          <div className="text-center p-2.5 bg-gray-50 rounded-lg">
            <p className="text-base font-bold" style={{ color: form.color }}>{form.converted}</p>
            <p className="text-xs text-gray-400 mt-0.5">Converted</p>
          </div>
        </div>

        {/* Conversion bar */}
        {form.submissions > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Submission rate</span>
              <span className="font-medium text-gray-600">{viewRate}%</span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(viewRate * 2, 100)}%`, backgroundColor: form.color }} />
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-2 mb-1">
              <span>Conversion rate</span>
              <span className={`font-medium ${convRate >= 40 ? 'text-emerald-600' : convRate >= 20 ? 'text-amber-600' : 'text-gray-600'}`}>{convRate}%</span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(convRate * 1.5, 100)}%`,
                  backgroundColor: convRate >= 40 ? '#10b981' : convRate >= 20 ? '#f59e0b' : '#94a3b8',
                }}
              />
            </div>
          </div>
        )}

        {/* Embed domain badge */}
        {form.embedDomain && (
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
            <i className="ri-global-line" />
            <span className="truncate">{form.embedDomain}</span>
            <span className="ml-auto px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-xs font-medium">Live</span>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-5 py-3 border-t border-gray-50 flex items-center gap-2">
        <button
          onClick={() => onViewResponses(form)}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap relative"
        >
          <i className="ri-inbox-line" />
          Responses
          {newCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white text-xs font-semibold px-1" style={{ backgroundColor: form.color, fontSize: '10px' }}>
              {newCount}
            </span>
          )}
        </button>
        <button
          onClick={() => onEmbed(form)}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-code-s-slash-line" />
          Embed
        </button>
        <button
          onClick={() => onEdit(form)}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap ml-auto"
        >
          <i className="ri-edit-line" />
          Edit
        </button>
        <button
          onClick={() => onToggleStatus(form.id)}
          className={`text-xs px-2.5 py-1.5 rounded-lg cursor-pointer whitespace-nowrap font-medium ${
            form.status === 'Active'
              ? 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              : 'text-white hover:opacity-90'
          }`}
          style={form.status !== 'Active' ? { backgroundColor: form.color } : {}}
        >
          {form.status === 'Active' ? 'Pause' : 'Activate'}
        </button>
      </div>
    </div>
  );
}
